import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { UpgradeDialog } from "@/components/UpgradeDialog";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const nav = useNavigate();
  // null = checking, false = signed out, object = signed in
  const [user, setUser] = useState(null);
  const [upgradeReason, setUpgradeReason] = useState(null);
  const [billing, setBilling] = useState({});

  useEffect(() => {
    if (user === null) return;
    api.billingStatus().then(setBilling).catch(() => {});
  }, [user]);

  const checkAuth = useCallback(() => api.me().then(setUser).catch(() => setUser(false)), []);

  useEffect(() => {
    // Returning from Google OAuth: AuthCallback exchanges session_id first
    if (window.location.hash?.includes("session_id=")) return;
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    await api.logout().catch(() => {});
    setUser(false);
    nav("/");
  };

  const startCheckout = async (interval = "month") => {
    if (!user) return nav(`/login?next=upgrade&interval=${interval}`);
    nav(`/checkout?interval=${interval}`);
  };

  const openPortal = async () => {
    try {
      window.location.href = (await api.stripePortal(window.location.origin)).url;
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const cancelPlan = async () => {
    try {
      setUser(await api.cancelSubscription());
      toast("Pro cancelled. You're back on the free plan.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const value = {
    user,
    setUser,
    checkAuth,
    logout,
    isPro: user?.plan === "pro",
    trialEligible: !user || billing.trial_eligible === true,
    billingReady: user === false || billing.trial_eligible !== undefined,
    startCheckout,
    cancelPlan,
    openPortal,
    billingMode: billing.mode,
    openUpgrade: (reason) => setUpgradeReason(reason || "Unlock the full sanctum."),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <UpgradeDialog reason={upgradeReason} onClose={() => setUpgradeReason(null)} onUpgrade={startCheckout} trialEligible={!user || billing.trial_eligible === true} />
    </AuthContext.Provider>
  );
};
